import { FC, useState, useEffect, useRef } from 'react';
import { TextField, Box, IconButton, Popover } from '@mui/material';
import { TaskLabel, labelColors } from '@demind-inc/core';
import { CheckOutlined } from '@mui/icons-material';
import { upperFirst } from 'lodash';
import { useTranslation } from 'react-i18next';
import BasicDialog from '../../../common/BasicDialog/BasicDialog';
import './CreateLabelDialog.scss';
import { MAX_LABEL_NAME_LENGTH } from '../constants';

interface CreateLabelDialogProps {
  visible: boolean;
  initialLabelName: string;
  initialLabelColor?: string;
  onClose: () => void;
  onSave: (label: Omit<TaskLabel, 'id'>) => void;
  isLoading?: boolean;
  existingLabels?: TaskLabel[];
}

const CreateLabelDialog: FC<CreateLabelDialogProps> = ({
  visible,
  initialLabelName,
  initialLabelColor,
  onClose,
  onSave,
  isLoading = false,
  existingLabels = [],
}) => {
  const { t } = useTranslation();
  const [labelName, setLabelName] = useState(initialLabelName);
  const [labelColor, setLabelColor] = useState<string>(
    initialLabelColor || Object.values(labelColors)[0] || '#680ddb'
  );
  const [colorPickerAnchor, setColorPickerAnchor] = useState<HTMLElement | null>(null);
  const colorButtonRef = useRef<HTMLButtonElement>(null);
  const [nameError, setNameError] = useState('');

  useEffect(() => {
    if (visible) {
      setLabelName(initialLabelName);
      const initialColor = initialLabelColor || Object.values(labelColors)[0] || '#680ddb';
      setLabelColor(initialColor);
      setNameError('');
    }
  }, [visible, initialLabelName, initialLabelColor]);

  useEffect(() => {
    setNameError('');
  }, [labelName]);

  const handleColorButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setColorPickerAnchor(event.currentTarget);
  };

  const handleColorPickerClose = () => {
    setColorPickerAnchor(null);
  };

  const handleSave = () => {
    const trimmedName = labelName.trim();
    if (!trimmedName) {
      setNameError(t('task.labels.nameRequired'));
      return;
    }

    if (trimmedName.length > MAX_LABEL_NAME_LENGTH) {
      setNameError(t('task.labels.nameTooLong', { count: MAX_LABEL_NAME_LENGTH }));
      return;
    }

    const nameExists = existingLabels.some(
      (label) => label?.name?.toLowerCase() === trimmedName.toLowerCase()
    );

    if (nameExists) {
      setNameError(t('task.labels.alreadyExists'));
      return;
    }

    setNameError('');
    onSave({
      name: trimmedName,
      color: labelColor,
    });
  };

  const handleClose = () => {
    setLabelName(initialLabelName);
    setLabelColor(initialLabelColor || Object.values(labelColors)[0] || '#680ddb');
    setColorPickerAnchor(null);
    setNameError('');
    onClose();
  };

  const handleColorSelect = (color: string) => {
    setLabelColor(color);
    handleColorPickerClose();
  };

  const colorOptions = Object.entries(labelColors);

  return (
    <>
      <BasicDialog
        visible={visible}
        title={t('task.labels.createNewLabelTitle')}
        ctaString={t('task.common.create')}
        isLoading={isLoading}
        onYes={handleSave}
        onClose={handleClose}
        width="sm"
      >
        <Box className="create-label-dialog__content">
          <Box className="create-label-dialog__input-container">
            <IconButton
              ref={colorButtonRef}
              onClick={handleColorButtonClick}
              className="create-label-dialog__color-button"
              size="small"
            >
              <div
                className="create-label-dialog__color-preview"
                style={{ backgroundColor: labelColor }}
              />
            </IconButton>
            <TextField
              fullWidth
              variant="standard"
              value={labelName}
              onChange={(e) => setLabelName(e.target.value)}
              placeholder={t('task.labels.enterLabelName')}
              autoFocus
              className="create-label-dialog__text-field"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isLoading) {
                  handleSave();
                }
              }}
            />
          </Box>
          <Box className="create-label-dialog__error-text">{nameError}</Box>
        </Box>
      </BasicDialog>
      <Popover
        open={Boolean(colorPickerAnchor)}
        anchorEl={colorPickerAnchor}
        onClose={handleColorPickerClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            className: 'create-label-dialog__color-picker-popover',
          },
        }}
      >
        <Box className="create-label-dialog__color-picker-container">
          <Box className="create-label-dialog__color-picker-label">{t('task.labels.colors')}</Box>
          <Box className="create-label-dialog__color-picker-list">
            {colorOptions.map(([name, color]) => (
              <Box
                key={name}
                className="create-label-dialog__color-picker-list-item"
                onClick={() => handleColorSelect(color)}
              >
                <Box className="create-label-dialog__color-picker-list-item__left">
                  <div
                    className="create-label-dialog__color-picker-list-item__color"
                    style={{ backgroundColor: color }}
                  />
                  <span className="create-label-dialog__color-picker-list-item__name">
                    {t(`task.labels.colorNames.${name}`, { defaultValue: upperFirst(name) })}
                  </span>
                </Box>
                {color === labelColor && (
                  <CheckOutlined className="create-label-dialog__color-picker-list-item__check" />
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default CreateLabelDialog;
